import React, { forwardRef, memo, useMemo, useState, useCallback } from 'react';
import { BOARD_SIZE } from '../game/engine';

const Board = forwardRef(function Board(
  { board, ghost, clearedCells, onCellClick, onCellHover, onBoardLeave, targetingMode, onBombClick },
  ref
) {
  const [bombTarget, setBombTarget] = useState(null);

  const clearedSet = useMemo(() => {
    const set = new Set();
    if (clearedCells) {
      for (const cell of clearedCells) {
        set.add(`${cell.row},${cell.col}`);
      }
    }
    return set;
  }, [clearedCells]);

  const ghostCells = useMemo(() => {
    if (!ghost || !ghost.piece) return new Map();
    const map = new Map();
    const { row, col, piece, valid } = ghost;
    for (let r = 0; r < piece.shape.length; r++) {
      for (let c = 0; c < piece.shape[r].length; c++) {
        if (piece.shape[r][c]) {
          map.set(`${row + r},${col + c}`, { color: piece.color, valid });
        }
      }
    }
    return map;
  }, [ghost]);

  const bombCells = useMemo(() => {
    if (targetingMode !== 'bomb' || !bombTarget) return new Set();
    const set = new Set();
    for (let r = bombTarget.row - 1; r <= bombTarget.row + 1; r++) {
      for (let c = bombTarget.col - 1; c <= bombTarget.col + 1; c++) {
        if (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE) {
          set.add(`${r},${c}`);
        }
      }
    }
    return set;
  }, [targetingMode, bombTarget]);

  const handleCellHover = useCallback((r, c) => {
    if (targetingMode === 'bomb') {
      setBombTarget({ row: r, col: c });
    } else {
      onCellHover(r, c);
    }
  }, [targetingMode, onCellHover]);

  const handleCellClick = useCallback((r, c) => {
    if (targetingMode === 'bomb' && onBombClick) {
      onBombClick(r, c);
      setBombTarget(null);
    } else {
      onCellClick(r, c);
    }
  }, [targetingMode, onBombClick, onCellClick]);

  const handleBoardLeave = useCallback(() => {
    setBombTarget(null);
    onBoardLeave();
  }, [onBoardLeave]);

  return (
    <div
      className={`board${targetingMode === 'bomb' ? ' board-bomb-targeting' : ''}`}
      ref={ref}
      onPointerLeave={handleBoardLeave}
      role="grid"
      aria-label="Game board"
    >
      {Array.from({ length: BOARD_SIZE }, (_, r) => (
        <div key={r} className="board-row" role="row">
          {Array.from({ length: BOARD_SIZE }, (_, c) => {
            const key = `${r},${c}`;
            const cellColor = board[r][c];
            const ghostData = ghostCells.get(key);
            const isClearing = clearedSet.has(key);
            const isBombTarget = bombCells.has(key);

            let className = 'cell';
            let style = {};

            if (isClearing && cellColor) {
              className += ' cell-clearing';
              style.backgroundColor = cellColor;
              style['--cell-color'] = cellColor;
            } else if (cellColor) {
              className += ' cell-filled';
              style.backgroundColor = cellColor;
              style['--cell-color'] = cellColor;
            } else if (ghostData) {
              className += ghostData.valid ? ' cell-ghost-valid' : ' cell-ghost-invalid';
              style.backgroundColor = ghostData.valid
                ? ghostData.color + '55'
                : '#ff000030';
              style['--cell-color'] = ghostData.color;
            }

            if (isBombTarget) {
              className += ' cell-bomb-target';
            }

            return (
              <div
                key={c}
                className={className}
                style={style}
                role="gridcell"
                aria-label={`Row ${r + 1}, Column ${c + 1}${cellColor ? ', occupied' : ', empty'}`}
                onClick={() => handleCellClick(r, c)}
                onPointerEnter={() => handleCellHover(r, c)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
});

export default memo(Board);
